import React, { FC, useRef } from 'react';
import { Button, ButtonLoading } from '../Button';
import styled from 'styled-components';
import { TextFieldErrorBanner } from '../TextField/TextField.styles';
import { Banner } from '../Banner';
import { Width } from '../../types';
import { Tooltip } from '../Tooltip';

export const TextActionContainer = styled.div<{ width?: Width }>`
  display: flex;
  flex-direction: column;
  gap: calc(var(--spacing-xxs) * 2);
  width: ${(p) => p.width ?? 'fit-content'};
`;

const TextActionInputContainer = styled.div`
  display: flex;

  > input {
    border-radius: var(--radius-8) 0 0 var(--radius-8);
    padding: var(--spacing-s);
    text-overflow: ellipsis;
    margin: unset;
    border: 1px solid var(--color-interface-layout-normal);
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    color: var(--color-interface-content-normal);
    outline: none;

    ::placeholder {
      color: var(--color-interface-content-lighter);
    }
  }

  > button {
    margin: unset;
    border-left: none;
    border-radius: unset;
    border-top-right-radius: var(--radius-100);
    border-bottom-right-radius: var(--radius-100);
  }
`;

const tooltipId = 'tooltipTextActionId';

export const TextActionErrorBanner = styled(Banner).attrs({
  severity: 'error',
})`
  padding: calc(var(--spacing-xxs)) var(--spacing-s);
  width: 100%;
  gap: calc(var(--spacing-xxs) * 2);
`;

type TextActionProps = {
  actionDisabled?: boolean;
  placeholder?: string;
  error?: Maybe<string>;
  text?: string;
  width?: string;
  actionLabel: string;
  onAction: F<string>;
  loading?: boolean;
  onChange?: F<string>;
  actionTooltip?: Maybe<string>;
};

export const TextAction: FC<TextActionProps> = ({
  actionDisabled,
  actionTooltip,
  placeholder,
  error,
  text,
  width = '100%',
  loading,
  actionLabel,
  onAction,
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleAction = () => {
    onAction(inputRef.current?.value ?? '');
  };
  return (
    <TextActionContainer width={width}>
      <TextActionInputContainer>
        <input
          placeholder={placeholder}
          ref={inputRef}
          value={text}
          onChange={(e) => onChange?.(e.target.value)}
        />
        <ButtonLoading
          data-tooltip-id={tooltipId}
          loading={loading}
          variant="secondary"
          onClick={handleAction}
          disabled={actionDisabled}
        >
          {actionLabel}
        </ButtonLoading>
        {actionTooltip && (
          <Tooltip variant="success" id={tooltipId} place={'right'}>
            Saved!
          </Tooltip>
        )}
      </TextActionInputContainer>
      {error && <TextActionErrorBanner>{error}</TextActionErrorBanner>}
    </TextActionContainer>
  );
};
